import { useState, useEffect } from 'react';
import { getSessionId } from '../../../services/riseAI';
import { getToken } from '../../../services/auth';
import { useAuth0 } from '../../../auth/useAuth0';

/**
 * Custom hook to manage RiseAI session ID
 * Fetches session ID when drawer opens and assetId is available
 */
export function useSessionId(open, assetId) {
  const [sessionId, setSessionId] = useState(null);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [sessionError, setSessionError] = useState(null);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const fetchSessionId = async () => {
      // Only fetch if drawer is open, assetId exists, and we don't already have a session ID
      if (!open || !assetId || sessionId) {
        return;
      }

      setIsLoadingSession(true);
      setSessionError(null);

      try {
        // Get Auth0 access token (preferred) or fallback to localStorage token
        let token = null;
        try {
          token = await getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0_AUDIENCE
          });
        } catch (authError) {
          console.warn('Failed to get Auth0 token, falling back to localStorage:', authError);
          token = getToken();
        }

        const sessionData = await getSessionId({
          appName: 'rise_ai_llm_agent',
          assetId: assetId,
          token: token
        });

        // Extract session ID from response
        // The API might return the session ID in different formats, so we handle multiple possibilities
        if (sessionData?.id) {
          setSessionId(sessionData.id);
        }
      } catch (error) {
        setSessionError('Failed to get session ID');
      } finally {
        setIsLoadingSession(false);
      }
    };

    fetchSessionId();
  }, [open, assetId, sessionId, getAccessTokenSilently]);

  return { sessionId, isLoadingSession, sessionError };
}
